import { AutoComplete, Button, Icons, Input, Tag } from '@pankod/refine-antd';
import { useEffect, useRef, useState } from 'react';
import styles from './style.module.css';

interface IOptionGroup {
  value: string;
  label: string | React.ReactNode;
}

interface IOptions {
  label: string | React.ReactNode;
  options: IOptionGroup[];
}

type SearchProps = {
  placeholder: string;
  className?: string;
  style?: React.CSSProperties;
  size: 'small' | 'middle' | 'large';
  options: IOptions[];
  onSelectOption: (searchText: string) => Promise<void>;
  onReset: () => Promise<void>;
};

export const Search: React.FC<SearchProps> = (props) => {
  const [searchText, setSearchText] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<IOptions[]>([]);
  const [tags, setTags] = useState<string[]>([]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    setFilteredOptions(props.options);
  }, [props.options]);

  useEffect(() => {
    // useEffect to run only on updates except initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    props.onSelectOption(tags.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  const onChange = (data: string) => {
    setSearchText(data);
  };

  const onSearch = (value: string) => {
    const filteredOptions = props.options.map((option) => {
      const { label, options } = option;
      const newOptions = options.filter((item) =>
        item.value.toLowerCase().includes(value.trim().toLowerCase())
      );
      return { label, options: newOptions };
    });

    setFilteredOptions(filteredOptions);
  };

  const onSelect = (data: string) => {
    if (!tags.includes(data)) {
      setTags((prevTags) => [...prevTags, data]);
    }
    setSearchText('');
  };

  const onResetClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setTags([]);
  };

  const handleCloseTag = (removedTag: string) => {
    const filteredTags = tags.filter((tag) => tag !== removedTag);
    setTags(filteredTags);
  };

  const allTags = () => {
    return tags.map((tag, index) => {
      return (
        <Tag
          key={index}
          color="blue"
          closable
          onClose={(e) => {
            e.preventDefault();
            handleCloseTag(tag);
          }}
        >
          {tag}
        </Tag>
      );
    });
  };

  return (
    <div className={styles['search-section']}>
      <div className={props.className} style={{ width: '100%', maxWidth: '600px' }}>
        <AutoComplete
          style={{ width: '100%', maxWidth: '400px' }}
          value={searchText}
          options={filteredOptions}
          onSelect={onSelect}
          onSearch={onSearch}
          onChange={onChange}
          suffixIcon={<Icons.ReloadOutlined />}
        >
          <Input
            size={props.size}
            placeholder={props.placeholder}
            suffix={<Icons.SearchOutlined />}
          />
        </AutoComplete>
        {!!tags.length && (
          <Button
            size={props.size}
            onClick={onResetClick}
            icon={<Icons.ReloadOutlined />}
            style={{ marginLeft: '0.5rem' }}
          />
        )}
      </div>

      <div style={{ marginTop: '1rem' }}>{allTags()}</div>
    </div>
  );
};
